import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, InputAdornment, OutlinedInput, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import type { AnalyticalVariableListItem } from '@verantos-dev/feature-analytical-variables';

import { AvLibraryList } from './AvLibraryList';
import type { AvIneligibilityReason } from '../../utils/avLibraryEligibility';

interface AvLibraryListWithSearchProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  items: AnalyticalVariableListItem[];
  isLoading: boolean;
  isError: boolean;
  multiple: boolean;
  onSelect: (avId: string) => void;
  isSelected: (avId: string) => boolean;
  /**
   * Bulk-selection handler. When provided in `multiple` mode, a Select all /
   * Clear all toggle renders beside the subheading; it receives the full set of
   * selectable ids to select, or an empty array to clear.
   */
  onSelectAll?: (avIds: string[]) => void;
  inCohortAvIds?: Set<string>;
  nameConflictAvIds?: Set<string>;
  /** Rows disabled because the cohort's conditional index cannot use them, keyed by AV id. */
  disabledReasons?: Map<string, AvIneligibilityReason>;
  maxHeight?: number;
  searchInputTestId?: string;
}

export const AvLibraryListWithSearch = ({
  searchQuery,
  onSearchChange,
  items,
  isLoading,
  isError,
  multiple,
  onSelect,
  isSelected,
  onSelectAll,
  inCohortAvIds,
  nameConflictAvIds,
  disabledReasons,
  maxHeight = 380,
  searchInputTestId,
}: AvLibraryListWithSearchProps) => {
  const intl = useIntl();

  // Selectable = everything not already in the cohort, not a name conflict and not disabled.
  // Event-level AVs (row_level="event") are filtered out of `items` upstream,
  // before this component ever sees them.
  const selectableIds = useMemo(
    () =>
      items
        .map((item) => item.analyticalVariableId)
        .filter(
          (id) =>
            !inCohortAvIds?.has(id) && !nameConflictAvIds?.has(id) && !disabledReasons?.has(id)
        ),
    [items, inCohortAvIds, nameConflictAvIds, disabledReasons]
  );

  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => isSelected(id));
  const showSelectAll = multiple && !!onSelectAll && selectableIds.length > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body1" fontWeight={500}>
          {intl.formatMessage(
            { id: 'projects.analyticalVariables.library.selectVariableWithCount' },
            { count: items.length }
          )}
        </Typography>
        {showSelectAll && (
          <Button
            variant="text"
            size="small"
            onClick={() => onSelectAll?.(allSelected ? [] : selectableIds)}
            sx={{ textTransform: 'none' }}
            data-testid="av-library-select-all"
          >
            {intl.formatMessage({
              id: allSelected
                ? 'projects.analyticalVariables.library.clearAll'
                : 'projects.analyticalVariables.library.selectAll',
            })}
          </Button>
        )}
      </Box>
      <OutlinedInput
        size="small"
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={intl.formatMessage({
          id: 'projects.analyticalVariables.library.searchPlaceholder',
        })}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        }
        data-testid={searchInputTestId}
      />
      <Box sx={{ overflowY: 'auto', maxHeight }}>
        <AvLibraryList
          multiple={multiple}
          isLoading={isLoading}
          isError={isError}
          searchQuery={searchQuery}
          items={items}
          onSelect={onSelect}
          isSelected={isSelected}
          inCohortAvIds={inCohortAvIds}
          nameConflictAvIds={nameConflictAvIds}
          disabledReasons={disabledReasons}
        />
      </Box>
    </Box>
  );
};
